import { createContext, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../utils/msalConfig";
import { RoleGroups } from "../roles/roleGroups"

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState(false);

  useEffect(() => {
    if (inProgress !== "none") {
      return;
    }

    if (!accounts || accounts.length === 0) {
      setLoading(false);
      return;
    }

    const fetchGroups = async () => {
      try {
        setLoading(true);
        // Acquire access token silently
        const tokenResp = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });

        const accessToken = tokenResp.accessToken;

        // Call Graph API: check membership for specific groups
        const res = await fetch(
          "https://graph.microsoft.com/v1.0/me/checkMemberGroups",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              groupIds: [RoleGroups.Admin, RoleGroups.Interviewer],
            }),
          }
        );

        const graphData = await res?.json();
        if (!res.ok) {
          throw new Error(graphData?.error?.message || `Graph API error ${res.status}`);
        }

        const userGroupIds = graphData.value || [];
        setGroups(userGroupIds);

        // Determine if user is in allowed groups
        const allowed = userGroupIds.some((id) =>
          Object.values(RoleGroups).includes(id)
        );
        setIsAllowed(allowed);
      } catch (err) {
        console.error("Failed to fetch user groups:", err);
        setGroups([]);
        setIsAllowed(false);

        // If interaction is required or token silent acquisition failed due to expired session
        if (
          err?.name === "InteractionRequiredAuthError" ||
          err?.errorMessage?.includes("interaction_required")
        ) {
          try {
            await instance.acquireTokenRedirect({
              ...loginRequest,
              account: accounts[0],
            });
            return;
          } catch (redirectErr) {
            console.error("Token redirect error:", redirectErr);
          }
        }

        // If session is expired or token cannot be retrieved, redirect to login for non-public routes
        const publicRoutes = [
          "/login",
          "/accessdenied",
          "/candidatefeedback/",
          "/candidate-form",
          "/success",
        ];
        const isPublic = publicRoutes.some((route) =>
          window.location.pathname.startsWith(route)
        );

        if (!isPublic) {
          window.location.href = "/login";
        }
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [accounts, instance, inProgress]);

  const hasGroup = (groupId) => groups.includes(groupId);

  return (
    <AuthContext.Provider
      value={{ groups, hasGroup, loading, isAllowed, setGroups }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);